import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computed } from 'mobx';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getDefaultSlrAppRibbonButtonStyles } from 'owa-command-ribbon-styles';
import { getMailMenuItemShouldShowMap } from 'owa-mail-filterable-menu-behavior';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import {
    onPrintClicked,
    onRibbonCustomizerClicked,
    onShowInImmersiveReaderClicked,
    onViewSettingsClicked,
} from '../../../../util/commandActions';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
} from '../../../../util/createMailRibbonButton';
import {
    getConversationsSections,
    getMessagePreviewSections,
    getLayoutSections,
    getDensitySections,
} from '../../../sections';

export const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();

const computeConversationsControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Conversations);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Conversations,
            controlInfo.text,
            controlInfo.tooltip,
            false,
            { sections: getConversationsSections() },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_View)],
                getKeytipString(MailRibbonControlId.Conversations),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getConversationsControl = () => computeConversationsControl.get();

const computeDensityControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Density);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Density,
            controlInfo.text,
            controlInfo.tooltip,
            false,
            { sections: getDensitySections() },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_View)],
                getKeytipString(MailRibbonControlId.Density),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getDensityControl = () => computeDensityControl.get();

const computeImmersiveReaderControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ImmersiveReader);
        return createMailAppButton(
            MailRibbonControlId.ImmersiveReader,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onShowInImmersiveReaderClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_View)],
                getKeytipString(MailRibbonControlId.ImmersiveReader)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getImmersiveReaderControl = () => computeImmersiveReaderControl.get();

const computeMessagePreviewControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.MessagePreview);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.MessagePreview,
            controlInfo.text,
            controlInfo.tooltip,
            false,
            { sections: getMessagePreviewSections() },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_View)],
                getKeytipString(MailRibbonControlId.MessagePreview),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getMessagePreviewControl = () => computeMessagePreviewControl.get();

const computeLayoutControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Layout);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Layout,
            controlInfo.text,
            controlInfo.tooltip,
            false,
            { sections: getLayoutSections(MailRibbonTabId.Tab_View) },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_View)],
                getKeytipString(MailRibbonControlId.Layout),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getLayoutControl = () => computeLayoutControl.get();

const computePrintControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Print);
        return createMailAppButton(
            MailRibbonControlId.Print,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onPrintClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_View)],
                getKeytipString(MailRibbonControlId.Print)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getPrintControl = () => computePrintControl.get();

const computeRibbonCustomizerControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RibbonCustomizer);
        return createMailAppButton(
            MailRibbonControlId.RibbonCustomizer,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            () => {
                onRibbonCustomizerClicked();
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_View)],
                getKeytipString(MailRibbonControlId.RibbonCustomizer)
            ),
            undefined /* icon */,
            undefined /* iconColor */,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getRibbonCustomizerControl = () => computeRibbonCustomizerControl.get();

const computeViewSettingsControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ViewSettings);
        return createMailAppButton(
            MailRibbonControlId.ViewSettings,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onViewSettingsClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_View)],
                getKeytipString(MailRibbonControlId.ViewSettings)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getViewSettingsControl = () => computeViewSettingsControl.get();
